import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getUserInterfacesFromStorage, expandInterfacesWithChildren } from '../../utils/permissionUtils';
import DailyReportsPage from './DailyReports/DailyReportsPage';
import MonthlyReportsPage from './MonthlyReports/MonthlyReportsPage';
import RealtimeReportsPage from './RealtimeReports/RealtimeReportsPage';
import AppsMediaReportsPage from './AppsMediaReports/AppsMediaReportsPage';
import EFormInstanceReportsPage from './EFormInstanceReports/EFormInstanceReportsPage';
import EFormInstancePivotPage from './EFormInstancePivot/EFormInstancePivotPage';
import CommerceReportsPage from './CommerceReports/CommerceReportsPage';
import UsageBillingSystemReportPage from './UsageBillingSystem/UsageBillingSystemReportPage';

const ReportsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [userInterfaces, setUserInterfaces] = useState([]);
  const [loading, setLoading] = useState(true);

  // 展開權限：API 回傳為原始列表（如 ["reports"]），勾選父層時須展開為含所有子項（reports.appsMedia、reports.eformInstances 等）再檢查
  const expandedInterfaces = useMemo(
    () => expandInterfacesWithChildren(userInterfaces || []),
    [userInterfaces]
  );

  // 載入用戶權限
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const interfaces = await getUserInterfacesFromStorage(true);
        setUserInterfaces(interfaces);
        
        const expanded = expandInterfacesWithChildren(interfaces || []);
        const hasReportsPermission = expanded.some(iface => 
          iface === 'reports' || iface.startsWith('reports.')
        );
        
        if (!hasReportsPermission) {
          message.warning(t('reports.noPermission') || '您沒有權限訪問報表功能');
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('載入權限失敗:', error);
        message.error(t('reports.loadPermissionFailed') || '載入權限失敗');
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [navigate, t]);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>{t('common.loading') || '載入中...'}</p>
      </div>
    );
  }

  // 根據路由路徑決定顯示哪個頁面
  const path = location.pathname;
  
  if (path === '/reports/daily') {
    const hasDailyPermission = expandedInterfaces.some(iface => 
      iface.startsWith('reports.daily')
    );
    if (!hasDailyPermission) {
      message.warning(t('reports.noDailyReportsPermission') || '您沒有權限訪問每日報表');
      navigate('/dashboard');
      return null;
    }
    return <DailyReportsPage userInterfaces={expandedInterfaces} />;
  } else if (path === '/reports/monthly') {
    const hasMonthlyPermission = expandedInterfaces.some(iface => 
      iface.startsWith('reports.monthly')
    );
    if (!hasMonthlyPermission) {
      message.warning(t('reports.noMonthlyReportsPermission') || '您沒有權限訪問每月報表');
      navigate('/dashboard');
      return null;
    }
    return <MonthlyReportsPage userInterfaces={expandedInterfaces} />;
  } else if (path === '/reports/realtime') {
    const hasRealtimePermission = expandedInterfaces.some(iface => 
      iface.startsWith('reports.realtime')
    );
    if (!hasRealtimePermission) {
      message.warning(t('reports.noRealtimeReportsPermission') || '您沒有權限訪問實時報表');
      navigate('/dashboard');
      return null;
    }
    return <RealtimeReportsPage userInterfaces={expandedInterfaces} />;
  } else if (path === '/reports/apps-media') {
    const hasAppsMediaPermission = expandedInterfaces.some(iface => 
      iface === 'reports.appsMedia' || 
      iface.startsWith('reports.appsMedia') ||
      iface === 'reports.daily.appsMedia'
    );
    if (!hasAppsMediaPermission) {
      message.warning(t('reports.noAppsMediaReportsPermission') || '您沒有權限訪問 App\'s Media Reports');
      navigate('/dashboard');
      return null;
    }
    return <AppsMediaReportsPage />;
  } else if (path === '/reports/eform-instances') {
    const hasEformInstancePermission = expandedInterfaces.some(iface =>
      iface === 'reports.eformInstances' ||
      iface.startsWith('reports.eformInstances')
    );
    if (!hasEformInstancePermission) {
      message.warning(t('reports.noEformInstanceReportsPermission') || '您沒有權限訪問表單實例報表');
      navigate('/dashboard');
      return null;
    }
    return <EFormInstanceReportsPage />;
  } else if (path === '/reports/eform-pivot') {
    const hasEformPivotPermission = expandedInterfaces.some(iface =>
      iface === 'reports.eformPivot' ||
      iface.startsWith('reports.eformPivot')
    );
    if (!hasEformPivotPermission) {
      message.warning(t('reports.noEformPivotPermission') || '您沒有權限訪問表單樞紐分析報表');
      navigate('/dashboard');
      return null;
    }
    return <EFormInstancePivotPage />;
  } else if (path === '/reports/commerce') {
    const hasCommerceReportsPermission = expandedInterfaces.some(iface =>
      iface === 'reports.commerce' ||
      iface.startsWith('reports.commerce.')
    );
    if (!hasCommerceReportsPermission) {
      message.warning(t('reports.noCommerceReportsPermission') || '您沒有權限訪問商務管理報表');
      navigate('/dashboard');
      return null;
    }
    return <CommerceReportsPage userInterfaces={expandedInterfaces} />;
  } else if (path === '/reports/billing-system') {
    const hasBillingSystem = expandedInterfaces.some(
      (iface) => iface === 'reports.billingSystem'
    );
    if (!hasBillingSystem) {
      message.warning(
        t('reports.noPermission') || t('usageBilling.noPermission') || '無權限'
      );
      navigate('/dashboard');
      return null;
    }
    return <UsageBillingSystemReportPage />;
  }

  // 默認重定向（使用展開後權限判斷）
  const hasDailyPermission = expandedInterfaces.some(iface => 
    iface.startsWith('reports.daily')
  );
  const hasMonthlyPermission = expandedInterfaces.some(iface => 
    iface.startsWith('reports.monthly')
  );
  const hasRealtimePermission = expandedInterfaces.some(iface => 
    iface.startsWith('reports.realtime')
  );
  const hasAppsMediaPermission = expandedInterfaces.some(iface => 
    iface === 'reports.appsMedia' || 
    iface.startsWith('reports.appsMedia') ||
    iface === 'reports.daily.appsMedia'
  );
  const hasEformInstancePermission = expandedInterfaces.some(iface =>
    iface === 'reports.eformInstances' ||
    iface.startsWith('reports.eformInstances')
  );
  const hasEformPivotPermission = expandedInterfaces.some(iface =>
    iface === 'reports.eformPivot' ||
    iface.startsWith('reports.eformPivot')
  );
  const hasCommerceReportsPermission = expandedInterfaces.some(iface =>
    iface === 'reports.commerce' ||
    iface.startsWith('reports.commerce.')
  );
  const hasBillingSystemPermission = expandedInterfaces.some(
    (iface) => iface === 'reports.billingSystem'
  );

  if (hasDailyPermission) {
    navigate('/reports/daily', { replace: true });
  } else if (hasMonthlyPermission) {
    navigate('/reports/monthly', { replace: true });
  } else if (hasRealtimePermission) {
    navigate('/reports/realtime', { replace: true });
  } else if (hasAppsMediaPermission) {
    navigate('/reports/apps-media', { replace: true });
  } else if (hasEformInstancePermission) {
    navigate('/reports/eform-instances', { replace: true });
  } else if (hasEformPivotPermission) {
    navigate('/reports/eform-pivot', { replace: true });
  } else if (hasCommerceReportsPermission) {
    navigate('/reports/commerce', { replace: true });
  } else if (hasBillingSystemPermission) {
    navigate('/reports/billing-system', { replace: true });
  } else {
    navigate('/dashboard', { replace: true });
  }
  
  return null;
};

export default ReportsPage;

